import { useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import {
  applyEvent,
  CommonOrCommonRetirementFlug,
  Status,
} from "../../assets/data/Constants";
import { GlobalContext } from "../../context/globalContext";
import TkKikoStepper from "../../components/molecules/TkKikoStepper";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkViewButtonJudgement from "../../components/molecules/TkViewButtonJudgement";
import TkMemo from "../../components/molecules/TkMemo";
import TkApprovalDateSymbolNumberRegistrationDialog from "../../components/molecules/TkApprovalDateSymbolNumberRegistrationDialog";
import TkTableWithNoLine from "../../components/molecules/TkTableWithNoLine";
import { TkGetCode } from "../../utils/TkGetCode";
import { ITkViewButtonJudgementStatus } from "../../type/componentsType/TkViewButtonJudgement";
import { IAPI_REC027_Response, IAPI_REC028_Response } from "../../type/apiType";
import {
  httpAPI_APP016,
  httpAPI_REC008,
  httpAPI_REC027,
  httpAPI_REC028,
} from "./api/request";
import { fakeTableList, headList, schema } from "./parts";
import "./_style.scss";
import _ from "lodash";
import { isStatusAfterApproval } from "../../utils/useTools";

/**
 * SCRW160004 新規請求
 * @return SCRW160004 新規請求
 */
const SCRW160004 = () => {
  const applyId = useLocation().state?.applyId;
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const [statecorpInfoList, setStatecorpInfoList] = useState<any>([]);
  const [status, setStatus] = useState<ITkViewButtonJudgementStatus>();
  const [memo, setMemo] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  // 初期表示
  const handleCallApiInit = async () => {
    const http = applyId ? httpAPI_REC028 : httpAPI_REC027;

    try {
      const res: IAPI_REC028_Response & IAPI_REC027_Response = await http({
        applyId,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        let resArr = stateFakeTableList.current;

        let disabled = true;
        if (!applyId || res.latestApplyStatus === Status["差戻し一時保存"]) {
          disabled = false;
        }

        resArr.forEach((item) => (item.disabled = disabled));

        for (let i in res) {
          let targetObj = resArr.find((item) => item.keyword === i)!;

          targetObj && (targetObj.setDefaultValue = res[i as keyof typeof res]);
        }

        // 処理状況リスト
        let memo = resArr.find((item) => item.keyword === "memo")!;
        memo.showLine = !!applyId;
        let statusList: any = res.statusList?.map((item) => [
          {
            children: item.managerName,
          },
          {
            children: item.process,
          },
          {
            children: item.createDate,
            align: "center",
          },
          {
            children: item.processComment,
          },
        ]);
        memo.node = (
          <TkMemo
            bodyList={statusList}
            showCheckBox={false}
            onChange={handleMemoChange}
          />
        );

        // 決裁日・記号番号
        let symbolNoList = [
          [{ children: res.decisionDate }, { children: res.symbolNo }],
        ];
        let symbolNo = resArr.find((item) => item.keyword === "symbolNo")!;
        symbolNo.showLine = isStatusAfterApproval(res.latestApplyStatus);
        symbolNo.node = (
          <TkTableWithNoLine headList={headList} bodyList={symbolNoList} />
        );

        !applyId &&
          (resArr.find(
            (item) => item.keyword === "billingPrefecture"
          )!.onGetValue = (value: any) => {
            let amt =
              res.billingList?.find((item) => value === item.billingPrefecture)
                ?.amt ?? 0;
            resArr.find((item) => item.keyword === "amt")!.setDefaultValue =
              amt;
            setStatecorpInfoList([...resArr]);
          });

        setStatus(res.latestApplyStatus as Status);

        setStatecorpInfoList([...resArr]);
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch {
      navigate("/error");
    }
  };

  useEffect(() => {
    handleCallApiInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMemoChange = (event: any) => {
    setMemo(event.target.value);
  };

  const handleSubmit = async (data: any) => {
    try {
      const res = await httpAPI_REC008({
        nendo: state.authInfo?.fiscalYear,
        billingPrefectureCd: data.billingPrefecture,
        billingDate: data.billingDate,
        amt: data.amt,
        decisionDate: data.paymentConfirmDate,
        depositDueDate: data.depositDueDate,
        status: "0",
        applyId: applyId,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        navigate("/SCRW160003");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleStatusUpdate = async (btnName: keyof typeof applyEvent) => {
    let event = applyEvent[btnName];

    switch (event) {
      case applyEvent["受理"]:
      case applyEvent["課長代理確認"]:
      case applyEvent["上長承認"]:
        handleCallApiUpdate(event);
        break;
      case applyEvent["担当者に差戻し"]:
        if (status === "01" || status === "02") {
          handleCallApiUpdate(applyEvent["契約者に差戻し"]);
        } else {
          handleCallApiUpdate(event);
        }
        break;
      case applyEvent["承認"]:
        setIsOpen(true);
        break;
    }
  };

  const handleCallApiUpdate = async (event: any, data?: any) => {
    // 申請状況更新API 呼び出し
    try {
      const res = await httpAPI_APP016({
        applyId,
        applyEvent: event,
        symbolNo: data
          ? TkGetCode(CommonOrCommonRetirementFlug, data?.symbolNumberType) +
            "第" +
            data?.symbolNumber +
            "号"
          : "",
        decisionDate: data?.settlementDate,
        screenId: "SCRW160004",
        reason: memo,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        navigate("/SCRW110001");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch {
      navigate("/error");
    }
  };

  return (
    <Box className="SCRW160004" data-testid="SCRW160004PageForTest">
      {applyId && (
        <TkKikoStepper
          applicationFlowType="TYPE2"
          businessType="JOB-51"
          applicationStatus={status}
        />
      )}
      <TkFakeTable
        list={statecorpInfoList}
        ruleSchema={schema}
        onCheckAndSubmit={handleSubmit}
        submitButtonText="要処理一覧に登録"
        showButton={!applyId || status === Status["差戻し一時保存"]}
        buttonGroup={
          <Box>
            <TkBackButton className="tkBottonMr" />
            {applyId && (
              <TkViewButtonJudgement
                flowType={"TYPE2"}
                businessType="JOB-51"
                Status={status}
                onClick={handleStatusUpdate}
              />
            )}
          </Box>
        }
      />

      <TkApprovalDateSymbolNumberRegistrationDialog
        open={isOpen}
        onSuccess={(e: any) => handleCallApiUpdate(applyEvent["承認"], e)}
        onClose={() => setIsOpen(false)}
      />
    </Box>
  );
};

export default SCRW160004;
